import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useAppSelector } from '../../../core/store/hooks';
import { selectCurrentUser } from '../../auth';
import { hasPermission, PERMISSIONS } from '@features/auth/utils/permissionRules';
import { generateAvatarUrl } from '../../../core/layouts/Sidebar/utils/avatar';
import { Logo, RatingButtons } from '@core/components/ui';
import { formatDateCompact } from '@core/components/ui/utils/formatters';
import { ChatCitationsList } from './ChatCitationsList';
import { ChatLegacyCitation } from './ChatLegacyCitation';
import { ChatLoadingIndicator } from './ChatLoadingIndicator';
import { useTypewriter } from '../hooks/useTypewriter';

function ChatMessageComponent({
  message,
  showTimestamp = true,
  enableTypewriter = false,
  // Rating props (optional - only for admin/superadmin)
  messageId = null,
  sessionId = null,
  ratings = null,
  onRate = null,
  onQuickReply = null,
  isLastMessage = false,
}) {
  const { t } = useTranslation();
  const userData = useAppSelector(selectCurrentUser);

  // Check if user is admin or superadmin
  const isAdminOrSuperAdmin = useMemo(
    () => hasPermission(userData?.groups, PERMISSIONS.ADMIN_ACCESS) ||
          hasPermission(userData?.groups, PERMISSIONS.CROSS_ACCOUNT_ACCESS),
    [userData?.groups]
  );

  // Memoize avatar to avoid recomputing on every render
  const userAvatar = useMemo(
    () => generateAvatarUrl(userData?.first_name, userData?.last_name),
    [userData?.first_name, userData?.last_name]
  );

  // Typewriter effect for bot response (only if enabled)
  const responseText = message.response || message.answer;
  const { displayedText } = useTypewriter(
    enableTypewriter ? responseText : '',
    2
  );

  // Use full text if typewriter is disabled, otherwise use typewriter text
  const finalText = enableTypewriter ? displayedText : responseText;

  return (
    <div className="p-2 animate-fadeIn">
      {/* User Question (hidden when empty, e.g. bot-only onboarding messages) */}
      {message.question && (
        <div className="flex gap-2 sm:gap-3 mb-3 sm:mb-4">
          <img
            className="size-6 sm:size-8 rounded-full shrink-0 object-cover"
            src={userAvatar}
            alt="User avatar"
          />
          <div className="flex-1">
            <div className="bg-brand/6 text-brand-dark rounded-xl px-3 py-2 sm:px-4 sm:py-3">
              <p className="break-words">{message.question}</p>
            </div>
            {showTimestamp && message.created_at && (
              <p className="text-xs text-muted mt-2">
                {formatDateCompact(message.created_at)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Bot Response - always show */}
      <div className="flex gap-2 sm:gap-3">
        <Logo
          variant="compact"
          className="size-6 sm:size-8 rounded-full shrink-0 ml-[0.14rem] text-brand"
        />
        <div className="flex-1 min-h-[5rem]">
          {!responseText ? (
            // Show skeleton loading while waiting for response
            <>
              <div className="space-y-3 animate-pulse mt-2">
                <div className="h-5 bg-brand/7 rounded-md w-3/4"></div>
                <div className="h-5 bg-brand/7 rounded-md w-full"></div>
              </div>
              <ChatLoadingIndicator />
            </>
          ) : (
            // Show actual response
            <>
              <div className="text-brand-dark prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                <ReactMarkdown
                  components={{
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-brand underline hover:text-brand cursor-pointer"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {finalText}
                </ReactMarkdown>
              </div>

              {/* Citations list */}
              <ChatCitationsList citations={message.citations} />

              {/* Backward compatibility: handle old single citation format */}
              <ChatLegacyCitation citation={!message.citations ? message.citation : null} />

              {/* Action button - only on last message */}
              {isLastMessage && message.quickReplies?.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {message.quickReplies.map((btn, idx) => (
                    btn.type === 'url' && btn.url ? (
                      <a
                        key={idx}
                        href={btn.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand border border-brand/30 rounded-lg hover:bg-brand/10 cursor-pointer transition-colors"
                      >
                        {btn.title}
                      </a>
                    ) : onQuickReply ? (
                      <button
                        key={idx}
                        onClick={() => onQuickReply(btn.title)}
                        className="px-3 py-1.5 text-sm font-medium text-brand border border-brand/30 rounded-lg hover:bg-brand/10 cursor-pointer transition-colors"
                      >
                        {btn.title}
                      </button>
                    ) : null
                  ))}
                </div>
              )}

              {showTimestamp && message.created_at && (
                <p className="text-xs text-muted mt-2">
                  {formatDateCompact(message.created_at)}
                </p>
              )}

              {/* Rating buttons - only for admin/superadmin */}
              {isAdminOrSuperAdmin && messageId && sessionId && onRate && (
                <div className="mt-2">
                  <RatingButtons
                    messageId={messageId}
                    sessionId={sessionId}
                    existingRatings={ratings || []}
                    onRate={onRate}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

ChatMessageComponent.propTypes = {
  message: PropTypes.shape({
    question: PropTypes.string.isRequired,
    response: PropTypes.string,
    answer: PropTypes.string,
    citations: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string,
      file: PropTypes.string,
      title: PropTypes.string,
      source_type: PropTypes.string,
      snippet: PropTypes.string,
      score: PropTypes.number,
    })),
    citation: PropTypes.shape({
      url: PropTypes.string,
      file: PropTypes.string,
    }),
    created_at: PropTypes.string,
  }).isRequired,
  showTimestamp: PropTypes.bool,
  enableTypewriter: PropTypes.bool,
  // Rating props (optional)
  messageId: PropTypes.string,
  sessionId: PropTypes.string,
  ratings: PropTypes.array,
  onRate: PropTypes.func,
  onQuickReply: PropTypes.func,
  isLastMessage: PropTypes.bool,
};

/**
 * Memoized ChatMessage component to prevent unnecessary re-renders
 * Custom equality check only re-renders if message content, typewriter state, or ratings change
 */
export const ChatMessage = React.memo(
  ChatMessageComponent,
  (prevProps, nextProps) => {
    // Check if message content is the same
    const messageEqual =
      prevProps.message.question === nextProps.message.question &&
      prevProps.message.response === nextProps.message.response &&
      prevProps.message.answer === nextProps.message.answer;

    // Check if ratings have changed (compare length and content)
    const prevRatings = prevProps.ratings || [];
    const nextRatings = nextProps.ratings || [];
    const ratingsEqual =
      prevRatings.length === nextRatings.length &&
      prevRatings.every((rating, index) =>
        rating.id === nextRatings[index]?.id &&
        rating.rating === nextRatings[index]?.rating
      );

    // Check if other props are the same
    const propsEqual =
      prevProps.showTimestamp === nextProps.showTimestamp &&
      prevProps.enableTypewriter === nextProps.enableTypewriter &&
      prevProps.messageId === nextProps.messageId &&
      prevProps.sessionId === nextProps.sessionId &&
      prevProps.isLastMessage === nextProps.isLastMessage;

    // Return true to skip re-render if everything is equal
    return messageEqual && ratingsEqual && propsEqual;
  }
);
